'use client'

import { cn } from '@repo/shared-client/lib/utils'
import { Link } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { isNonProductionEnvironment } from '@web/src/lib/app-environment'
import { openCookieSettings } from '@web/src/lib/cookie-consent/cookie-consent-store'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import type { ReactNode } from 'react'

const footerLinkClassName = 'text-sm transition-colors underline-offset-2 hover:text-foreground text-muted-foreground '

const ListHeader = ({ children }: { children: ReactNode }) => {
  return <p className="mb-2 font-bold text-md">{children}</p>
}

const FooterColumn = ({ children, title }: { children: ReactNode; title: string }) => {
  return (
    <div className="flex w-full flex-col items-center gap-3 md:items-start md:gap-2">
      <ListHeader>{title}</ListHeader>
      {children}
    </div>
  )
}

const DividerLine = ({ className = '' }: { className?: string }) => {
  return (
    <div
      className={`h-1 w-full bg-[linear-gradient(to_right,#FF000000,#55555540,#FF000000)] dark:bg-[linear-gradient(to_right,#FF000000,#555555FF,#FF000000)] ${className}`.trim()}
    />
  )
}

const Footer = () => {
  const { t } = useTranslation()

  return (
    <footer className="w-full">
      <div className="w-full px-6 py-6">
        <DividerLine className="mb-2" />
        <div className="flex flex-col gap-8 pb-4 md:flex-row md:items-stretch md:gap-12">
          <div className={cn('flex shrink-0 items-center justify-center', isNonProductionEnvironment() && 'md:w-80')}>
            <LogoFull size="sm" />
          </div>
          <div className="grid w-full min-w-0 flex-1 grid-cols-2 justify-items-center gap-6 text-center md:justify-items-start md:text-left lg:grid-cols-4">
            <FooterColumn title={t('Legal')}>
              <Link className={footerLinkClassName} to="/helpcenter/legal/terms-of-use">
                {t('Terms of Use')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/acceptable-use-policy">
                {t('Acceptable Use Policy')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/2257-exemption">
                {t('2257 Exemption')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/legal-requests">
                {t('Legal Requests')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/content-complaints-and-appeals">
                {t('Content Complaints and Appeals')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/copyright-complaints">
                {t('Copyright Complaints')}
              </Link>
            </FooterColumn>
            <FooterColumn title={t('Privacy')}>
              <Link className={footerLinkClassName} to="/helpcenter/privacy-policy">
                {t('Privacy Policy')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/cookie-policy">
                {t('Cookie Policy')}
              </Link>
              <button
                className={`${footerLinkClassName} cursor-pointer appearance-none border-0 bg-transparent p-0`}
                onClick={openCookieSettings}
                type="button"
              >
                {t('Cookie Settings')}
              </button>
            </FooterColumn>
            <FooterColumn title={t('Store')}>
              <Link className={footerLinkClassName} to="/helpcenter/legal/store-and-membership-terms">
                {t('Store and Membership Terms')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/shipping-and-delivery">
                {t('Shipping and Delivery')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/legal/returns-refunds-and-cancellations">
                {t('Returns, Refunds, and Cancellations')}
              </Link>
            </FooterColumn>
            <FooterColumn title={t('Information')}>
              <Link className={footerLinkClassName} to="/helpcenter">
                {t('Help Center')}
              </Link>
              <Link className={footerLinkClassName} to="/helpcenter/whatsnew/latest">
                {t("What's New")}
              </Link>
              <Link className={footerLinkClassName} to="/contact">
                {t('Contact Us')}
              </Link>
            </FooterColumn>
          </div>
        </div>
        <DividerLine className="mb-2" />
        <div className="flex justify-center">
          <p className="text-center text-muted-foreground text-sm">
            {t('© 2026 Spicy Friends Ltd., All rights reserved')} <br />
            {t('Version {{version}}', { version: env.VITE_APP_VERSION })}
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
